import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

import { ContainerIdentifierWithIndex } from '../container-structure';

import {
  getEntryMenuButtonSelectorByContainerIdAndIndex,
  getItemElementId,
  getItemMenuButtonId,
  getSectionMenuButtonId,
} from './focus-utils';

@Injectable({
  providedIn: 'root',
})
export class FocusService {
  constructor(@Inject(DOCUMENT) private document: Document) {}

  getElementById(elementId: string): HTMLElement | null {
    return this.document.getElementById(elementId);
  }

  getElementBySelector(selector: string): HTMLElement | null {
    return this.document.querySelector<HTMLElement>(selector);
  }

  getNthElementBySelector(selector: string, index: number): HTMLElement | null {
    const nodes = this.document.querySelectorAll<HTMLElement>(selector);
    if (nodes && nodes[index]) {
      return nodes[index];
    }
    return null;
  }

  /**
   * Focus on an element in the dom by its ID, if it exists.
   *
   * Used when closing revealed content to return input focus to the component that revealed it,
   * for accessibility reasons. This includes modals and custom widgets, like the inline-note-editor component.
   * See [WCAG 2.4.3](https://www.w3.org/WAI/WCAG21/Understanding/focus-order.html) for more information.
   *
   * @param {string} elementId The ID of the element to focus on
   * @returns {boolean} Whether the element was successfully focussed on
   */
  focusOnElementById(elementId: string): boolean {
    const elementToFocus = this.getElementById(elementId);
    if (elementToFocus) {
      elementToFocus.focus();
      return true;
    }
    return false;
  }

  /**
   * Focus on an element in the dom given its selector, if it exists.
   *
   * @param {string} selector The selector for the element to focus on
   * @returns {boolean} Whether the element was successfully focussed on
   */
  focusOnElementBySelector(selector: string, index?: number): boolean {
    const elementToFocus = index
      ? this.getNthElementBySelector(selector, index)
      : this.getElementBySelector(selector);

    if (elementToFocus) {
      elementToFocus.focus();
      return true;
    }
    return false;
  }

  /**
   * Scroll to a given element in the dom, if it exists.
   *
   * Note that we deliberately do not use ViewportScroller because it uses querySelector,
   * which only works with valid IDs. Our section IDs don't meet the validity requirements.
   *
   * @param {string} elementId The ID of the element to scroll to
   * @returns {boolean} Whether the element was successfully scrolled to
   */
  scrollToElement(elementId: string): boolean {
    const element = this.getElementById(elementId);
    if (element) {
      element.scrollIntoView();
      return true;
    }
    return false;
  }

  focusOnItemMenu(itemId: string) {
    const itemMenuButtonId = getItemMenuButtonId(itemId);
    const success = this.focusOnElementById(itemMenuButtonId);
    if (!success) {
      // Item may be outside of viewport and virtualised
      // so scroll to it first before attempting to focus
      this.scrollToElement(getItemElementId(itemId));
      this.focusOnElementById(itemMenuButtonId);
    }
  }

  /**
   * Return the nearest focusable list entry to an element given its container and index,
   * if one can be found.
   *
   * @param {ContainerIdentifierWithIndex} containerIdWithIndex Container and index for the element
   * @returns {HTMLElement | null}
   */
  getNearestEntryElement({
    id,
    type,
    index,
  }: ContainerIdentifierWithIndex): HTMLElement | null {
    const nextItemMenuButton = this.getElementBySelector(
      getEntryMenuButtonSelectorByContainerIdAndIndex(id, index),
    );
    if (nextItemMenuButton) {
      return nextItemMenuButton;
    }

    if (index !== 0) {
      const previousItemMenuButton = this.getElementBySelector(
        getEntryMenuButtonSelectorByContainerIdAndIndex(id, index - 1),
      );
      if (previousItemMenuButton) {
        return previousItemMenuButton;
      }
    }

    if (type === 'sections') {
      const sectionMenuButton = this.getElementById(getSectionMenuButtonId(id));
      if (sectionMenuButton) {
        return sectionMenuButton;
      }
    } else {
      const emptyListTip = this.getElementById(
        'editContainer-emptyListTip-addResource',
      );
      if (emptyListTip) {
        return emptyListTip;
      }
    }

    return null;
  }
}
